import React, { useEffect, useContext, useState } from "react";
import DialogPopup from "@_components/DialogPopup/DialogPopup";
import Button from "@mui/material/Button";
import { useDispatch, useSelector, ReactReduxContext } from "react-redux";
import { caseActions } from "@_actions/case.actions";
import { casesActions } from "@_actions/cases.actions";
import { caseActivityActions } from "@_actions/caseActivity.actions";
import { formatDatetime, getCurrencySign, stateError } from "@_utils/helpers";
import styles from "./ActivateCasePopup.module.css";
import NoImage from "@_assets/images/noimage.png";
import Scrollbar from "react-perfect-scrollbar-z";
import "scrollbar.css";

function ActivateCasePopup(props) {
  const { store } = useContext(ReactReduxContext);
  const { data, caseId } = props;
  const caseReducer = useSelector((state) => {
    return state.caseReducer;
  });
  const dispatch = useDispatch();
  const [selectedTax, setSelectedTax] = useState("");

  const actionClicked = () => {
    dispatch(caseActions.updateCaseStateActivate(caseId));
    setTimeout(() => {
      const currState =
        store.getState().caseReducer.status.CASE_UPDATE_STATE_ACTIVATE;

      if (currState === "success") {
        dispatch(casesActions.getCases());
        dispatch(casesActions.getCasesCount());
        dispatch(caseActions.getCaseWithChargeDetails(caseId, data.overview));
        /* Activating a case creates new activities (state change, first
        scheduled email, etc.). Refetch them directly here instead of relying
        on CaseActivitySection's effect to re-fire from a changed `data`
        reference -- that indirect coupling silently breaks when deps narrow. */
        dispatch(caseActivityActions.getCaseActivities(caseId));
        props.close();
      }
    }, 200);
  };

  const closePopup = () => {
    dispatch(caseActions.clearStateMessage("CASE_UPDATE_STATE_ACTIVATE"));
    props.close();
  };

  /**
   * Handles Activate Case success flow: closes popup, refreshes lists/counts, refetches case, clears status.
   * @why
   * Keeps UI consistent after activation by syncing global case lists and the current case details.
   * @when/howOften
   * Currently runs on EVERY render (no dependency array). It only acts when
   * `caseReducer.status.CASE_UPDATE_STATE_ACTIVATE === "success"`.
   * @listeningComponents
   * Also observed in OverviewSection (reset/activate effects) which reacts to
   * `CASE_UPDATE_STATE_ACTIVATE` for list refresh, case refetch, and notifications.
   * See OverviewSection.js around the activation/reset effects.
   * @threats
   * - Missing dependency array => runs each render; risk of repeated dispatches if status stays "success".
   * - Potential double-refresh with OverviewSection when both are mounted.
   * - Uses `data.overview` without guarding against undefined.
   * @fix
   * - Add dependency array: [caseReducer.status.CASE_UPDATE_STATE_ACTIVATE, dispatch, caseId, data.overview, props]
   * - Consider moving list refreshes to a single place (OverviewSection) to avoid duplicates.
   * - Guard `data.overview` or use `data?.overview` to prevent undefined access.
   */
  useEffect(() => {
    if (caseReducer.status.CASE_UPDATE_STATE_ACTIVATE === "success") {
      props.close();
      dispatch(casesActions.getCases());
      dispatch(casesActions.getCasesCount());
      dispatch(caseActions.getCaseWithChargeDetails(caseId, data.overview));
      /* Same reason as in actionClicked: refetch activities directly so the
      new state-change / scheduled-email cards appear without a manual reload. */
      dispatch(caseActivityActions.getCaseActivities(caseId));
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_STATE_ACTIVATE"));
    }
  });

  /**
   * Derives the display label for indirect tax based on charge details.
   * @why
   * Keeps the UI label in sync with `data.charge_details.indirect_tax_type`.
   * @when/howOften
   * Runs when `data.charge_details` changes (prop update from parent or refetch).
   * @threats
   * - If `data.charge_details` is undefined/null, current code falls through to empty string (OK).
   * - Dependency uses entire object; if parent recreates it on every render, this effect will rerun
   *   unnecessarily (minor performance issue).
   * @fix
   * - Narrow dependency to `data.charge_details?.indirect_tax_type` if stable.
   * - use switch statement for cleaner code
   */
  useEffect(() => {
    if (
      data.charge_details &&
      data.charge_details.indirect_tax_type === "SALES_TAX"
    ) {
      setSelectedTax("Sales Tax");
    } else if (
      data.charge_details &&
      data.charge_details.indirect_tax_type === "VAT"
    ) {
      setSelectedTax("VAT");
    } else if (
      data.charge_details &&
      data.charge_details.indirect_tax_type === "GST"
    ) {
      setSelectedTax("GST");
    } else setSelectedTax("");
  }, [data.charge_details]);

  return (
    <DialogPopup
      onKeyUp={(e) => {
        if (e.key === "Enter") {
          actionClicked();
        }
      }}
      title="Activate Case"
      open={props.open}
      onClose={() => props.close()}
    >
      <Scrollbar maxHeight={"calc(100vh - 174px)"} >
        <div className={styles.content_container}>
          <div className={styles.findings_container}>
            {data.findings &&
              data.findings.map((finding, index) => (
                <div key={index}>
                  {finding.license && finding.license.state === "VALID" ? (
                    <></>
                  ) : (
                    <div className={styles.finding_container} key={index}>
                      <div className={styles.finging_image_container}>
                        <img
                          className={styles.finging_image}
                          src={
                            finding.original_image.original_image_url || NoImage
                          }
                          alt={"Finding result"}
                        />
                      </div>
                      <div className={styles.finging_details_container}>
                        <p className={styles.finding_id}>
                          {finding.original_image.filename}
                        </p>
                      </div>
                      <div className={styles.finging_price_container}>
                        <div className={styles.price_label}>License Fee</div>
                        <div className={styles.price_value}>
                          {getCurrencySign(data.claim_details.currency)}{" "}
                          {finding.price}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>
          <div className={styles.total_container}>
            <div className={styles.total_label}>Net License fee</div>
            <div className={styles.total_value}>
              {getCurrencySign(data.claim_details.currency)}{" "}
              {data.charge_details.total_net_amount_findings}
            </div>
          </div>

          {data.charge_details.additional_fees_no_settlement &&
            data.charge_details.additional_fees_no_settlement.map(
              (fee, index) => (
                <div className={styles.price_row} key={index}>
                  <div key={1} className={styles.price_container}>
                    <div className={styles.price_label}>Name</div>
                    <div className={styles.price_value}>{fee.name}</div>
                  </div>
                  <div key={2} className={styles.price_container}>
                    <div className={styles.price_label}>Percentage</div>
                    <div className={styles.price_value}>{fee.percentage} %</div>
                  </div>
                  <div key={3} className={styles.price_container}>
                    <div className={styles.price_label}>Additional Fee</div>
                    <div className={styles.price_value}>
                      {getCurrencySign(data.claim_details.currency)}{" "}
                      {fee.amount}
                    </div>
                  </div>
                  <br />
                </div>
              ),
            )}
          <hr className={styles.activate_hr} />
          <div className={styles.price_row}>
            <div key={4} className={styles.price_container}>
              {data.charge_details.indirect_tax_type === "NO_INDIRECT_TAX" ? (
                <div className={styles.price_label}>No Indirect Tax</div>
              ) : (
                <>
                  <div className={styles.price_label}>{selectedTax}</div>
                  <div className={styles.price_value}>
                    {(data.charge_details.indirect_tax_settlement &&
                      data.charge_details.indirect_tax_settlement.percentage) ||
                      (data.charge_details.indirect_tax_no_settlement &&
                        data.charge_details.indirect_tax_no_settlement
                          .percentage)}{" "}
                    %
                  </div>
                </>
              )}
            </div>
            <div key={5} className={styles.price_container}>
              <div className={styles.price_label}>Claim Opponent Country</div>
              <div className={styles.price_value}>{data.country}</div>
            </div>
            <div key={6} className={styles.price_container}>
              {data.charge_details.indirect_tax_type !== "NO_INDIRECT_TAX" && (
                <>
                  {" "}
                  <div className={styles.price_label}>{selectedTax} Amount</div>
                  <div className={styles.price_value}>
                    {getCurrencySign(data.claim_details.currency)}{" "}
                    {(data.charge_details.indirect_tax_settlement &&
                      data.charge_details.indirect_tax_settlement.amount) ||
                      (data.charge_details.indirect_tax_no_settlement &&
                        data.charge_details.indirect_tax_no_settlement.amount)}
                  </div>
                </>
              )}
            </div>
          </div>
          <hr className={styles.activate_hr} />
          <div className={styles.price_row}>
            <div key={7} className={styles.price_container}>
              <div className={styles.price_label}>Discount</div>
              <div className={styles.price_value}>
                {(data.charge_details.settlement_offer &&
                  data.charge_details.settlement_offer.settlement_percentage) ||
                  "--"}{" "}
                %
              </div>
            </div>
            <div key={8} className={styles.price_container}>
              <div className={styles.price_label}>Discount Deadline</div>
              <div className={styles.price_value}>
                {data.charge_details.settlement_offer &&
                  formatDatetime(
                    data.charge_details.settlement_offer.settlement_date,
                    "short",
                  )}{" "}
              </div>
            </div>
          </div>

          <div className={styles.total_container}>
            <div className={styles.total_label}>
              Gross incl.
              {data.charge_details.indirect_tax_type !== "NO_INDIRECT_TAX" &&
                selectedTax + " & "}
              Fees
            </div>
            <div className={styles.total_value}>
              {getCurrencySign(data.claim_details.currency)}{" "}
              {data.charge_details.final_amount_gross_no_settlement}
            </div>
            <br />
            {data.charge_details.final_amount_gross_settlement && (
              <>
                <div className={styles.total_label}>
                  Discounted gross incl.
                  {data.charge_details.indirect_tax_type !==
                    "NO_INDIRECT_TAX" && selectedTax + " & "}
                  Fees
                </div>

                <div className={styles.total_value}>
                  {getCurrencySign(data.claim_details.currency)}{" "}
                  {data.charge_details.final_amount_gross_settlement || "--"}
                </div>
              </>
            )}
          </div>
        </div>
        </Scrollbar>
        {stateError(caseReducer.status.CASE_UPDATE_STATE_ACTIVATE) && (
          <div className={styles.error_container}>
            <div className={styles.error_text}>
              {stateError(caseReducer.status.CASE_UPDATE_STATE_ACTIVATE)}
            </div>
          </div>
        )}

        <div className="action_container">
          <Button className="btn_cancel" onClick={closePopup}>
            Cancel
          </Button>
          <Button className="btn_confirm" onClick={actionClicked}>
            Activate Case
          </Button>
        </div>
    </DialogPopup>
  );
}

export default ActivateCasePopup;
